import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Car } from '../../../../models/car';
import { CarStatus } from '../../../../models/enums';

@Component({
  selector: 'app-cars-list-cards',
  imports: [CommonModule, PaginatorModule, RouterLink],
  templateUrl: './cars-list-cards.html',
  styleUrl: './cars-list-cards.scss'
})
export class CarsListCards {

  @Input() cars: Car[] = [];
  @Input() totalRecords = 0;
  @Input() rows = 20;
  @Input() first = 0;

  @Output() pageChange = new EventEmitter<PaginatorState>();

  private router = inject(Router);

  navigate(car: Car) {
    this.router.navigate([`vehicles/${car.id}`]);
  }

  onCardClick(event: MouseEvent, car: Car) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.navigate(car);
  }

  getFuelPct(car: Car): number {
    return Math.min(Math.max(parseFloat(car.telemetry?.fuelLevel ?? '0') || 0, 0), 100);
  }

  getBatteryPct(car: Car): number {
    if (!car.telemetry?.powerSupplyVoltage) return 0;
    return Math.min((car.telemetry.powerSupplyVoltage / 12) * 100, 100);
  }

  getProgressColor(value: number): string {
    if (value >= 70) return 'var(--dark-green-background)';
    if (value >= 30) return 'var(--background-progress-bar)';
    return 'var(--status-discharged-text)';
  }

  getStatusStyle(status: CarStatus): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'AVAILABLE':           { background: 'var(--status-available-bg)',      color: 'var(--status-available-text)' },
      'IN_USE':              { background: 'var(--status-in-use-bg)',          color: 'var(--status-in-use-text)' },
      'RESERVED':            { background: 'var(--status-reserved-bg)',        color: 'var(--status-reserved-text)' },
      'MAINTENANCE':         { background: 'var(--status-maintenance-bg)',     color: 'var(--status-maintenance-text)' },
      'DISCHARGED':          { background: 'var(--status-discharged-bg)',      color: 'var(--status-discharged-text)' },
      'REBALANCING':         { background: 'var(--status-rebalancing-bg)',     color: 'var(--status-rebalancing-text)' },
      'NEEDS_INVESTIGATION': { background: 'var(--status-investigate-bg)',     color: 'var(--status-investigate-text)' },
      'TRANSPORTATION':      { background: 'var(--status-transportation-bg)',  color: 'var(--status-transportation-text)' },
      'INTERNAL_RENT':       { background: 'var(--status-internal-rent-bg)',   color: 'var(--status-internal-rent-text)' },
      'NOT_READY':           { background: 'var(--status-not-ready-bg)',       color: 'var(--status-not-ready-text)' },
      'CLEANING':            { background: 'var(--status-cleaning-bg)',        color: 'var(--status-cleaning-text)' },
      'NEED_CLEANING':       { background: 'var(--status-need-cleaning-bg)',   color: 'var(--status-need-cleaning-text)' },
      'STOLEN':              { background: 'var(--status-stolen-bg)',          color: 'var(--status-stolen-text)' },
      'TOTAL_LOSS':          { background: 'var(--status-total-loss-bg)',      color: 'var(--status-total-loss-text)' },
      'OPS_USE':             { background: 'var(--status-ops-use-bg)',         color: 'var(--status-ops-use-text)' },
    };
    return map[status as string] ?? { background: 'var(--status-reserved-bg)', color: 'var(--status-reserved-text)' };
  }
}
